import { lexer } from './lexer';
import { parser } from './parser';
import structure, { isValidReference } from './structure';
import { Reference } from './structure.types';
import { Node } from './parser.types';
import { Token } from './lexer.types';

interface Options {
    validate?: boolean; // whether to validate that the reference is real
}

/**
 * Main entry into parser. Takes a string and returns a list of references,
 * each of which is a book, chapter, and verse. Ordered by book, chapter, verse.
 * There is no text associated with the reference, just the book name in english.
 */
export function parseInput(input: string, options?: Options): Reference[] {
    const p = parser(<Token[]>lexer(input));
    const refs = collectReferences(p);
    if (options?.validate) {
        refs.forEach(isValidReference); // throws exception if invalid
        if (refs.length === 0) {
            throw new Error('No references found');
        }
    }
    return refs;
}

export function collectReferences(bookNodes: Node[]): Reference[] {
    const refs: Reference[] = [];
    // We could have many top, level nodes. We'll just process them one at a
    // time, which hopefully corresponds to the order they were parsed. We might
    // optionally sort within a book by chapter/verse, but I think we need to
    // leave the book order as it was in the original input.
    //
    // Nodes should have three levels of children:
    // 1. book
    // 2. chapter
    // 3. verse
    //
    bookNodes.forEach((book) => {
        book.children.forEach((chapter) => {
            chapter.children.forEach((verse) => {
                // TODO: need to handle error cases and throw errors.
                const ref = {
                    id:
                        Number(book?.value ?? 0) * 1000000 +
                        Number(chapter?.value ?? 0) * 1000 +
                        Number(verse?.value ?? 0),
                    bookName: book.value
                        ? String(structure[Number(book.value)]?.name)
                        : 'Unknown',
                    book: Number(book?.value ?? 0),
                    chapter: Number(chapter?.value ?? 0),
                    verse: Number(verse?.value ?? 0),
                };
                refs.push(ref);
            });
        });
    });
    return refs;
}

export default parseInput;
export type { Reference } from './structure.types';